'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Mic2, Zap, Play, CheckCircle2 } from 'lucide-react';

const liveStats = [
  { label: 'Active Sessions', value: '8,247', color: 'text-green-400' },
  { label: 'Live Viewers', value: '342K', color: 'text-cyan-400' },
  { label: 'Clips Generated Today', value: '14,891', color: 'text-purple-400' },
];

const participants = [
  { name: 'Marcus', role: 'Host', initials: 'MR', color: 'from-purple-500 to-pink-500', speaking: true },
  { name: 'Alicia', role: 'Co-Host', initials: 'AT', color: 'from-cyan-500 to-blue-500', speaking: false },
  { name: 'Devon', role: 'Guest', initials: 'DK', color: 'from-amber-500 to-orange-500', speaking: false },
  { name: 'Priya', role: 'Guest', initials: 'PS', color: 'from-green-500 to-teal-500', speaking: false },
];

export default function HeroSection() {
  const [recTime, setRecTime] = useState(0);
  const [speakingIndex, setSpeakingIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setRecTime((t) => t + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setSpeakingIndex((i) => (i + 1) % participants.length);
    }, 2800);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (s: number) => {
    const h = Math.floor(s / 3600).toString().padStart(2, '0');
    const m = Math.floor((s % 3600) / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${h}:${m}:${sec}`;
  };

  return (
    <section className="relative min-h-screen flex items-center pt-14 sm:pt-16 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-purple-950/30 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] sm:w-[800px] h-[400px] sm:h-[600px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-20 right-0 sm:right-10 w-[200px] sm:w-[400px] h-[200px] sm:h-[400px] bg-accent/5 rounded-full blur-3xl pointer-events-none" />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 w-full py-10 sm:py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left: Copy */}
          <div className="space-y-6 sm:space-y-8">
            {/* Badge */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-semibold text-primary">
                <Zap size={12} className="fill-current" />
                AI-Powered Creator Platform
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 text-xs font-semibold text-green-400">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full live-pulse" />
                {liveStats[0].value} Active Now
              </span>
            </div>

            {/* Headline */}
            <div className="space-y-3 sm:space-y-4">
              <h1 className="text-4xl sm:text-5xl xl:text-6xl 2xl:text-7xl font-extrabold leading-[1.05] tracking-tight">
                Record, Edit &{' '}
                <span className="gradient-text">Distribute</span>
                <br className="hidden sm:block" />
                {' '}Like a Studio
              </h1>
              <p className="text-base sm:text-lg xl:text-xl text-muted-foreground leading-relaxed max-w-lg">
                WebRTC remote recording in 4K, AI-generated clips in under 30 seconds, and one-click distribution to every platform — all in one workspace.
              </p>
            </div>

            {/* Feature bullets */}
            <div className="grid grid-cols-1 xs:grid-cols-2 gap-2 sm:gap-3">
              {[
                'Multi-guest 4K recording',
                'AI clips in <30 seconds',
                'Sub-2s stream latency',
                'Auto-transcript & SEO',
                'Cross-platform distribution',
                'Monetization built-in',
              ].map((feature) => (
                <div key={feature} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                  <CheckCircle2 size={15} className="text-green-400 flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col xs:flex-row flex-wrap items-stretch xs:items-center gap-3 sm:gap-4">
              <Link
                href="/dashboard"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl transition-all duration-150 active:scale-95 shadow-lg shadow-primary/20 touch-manipulation text-sm sm:text-base"
              >
                <Mic2 size={18} />
                Launch Your Studio
              </Link>
              <Link
                href="/studio-interface"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-card hover:bg-muted border border-border text-foreground font-semibold rounded-xl transition-all duration-150 active:scale-95 touch-manipulation text-sm sm:text-base"
              >
                <Play size={18} className="text-muted-foreground" />
                Watch Demo
              </Link>
            </div>

            {/* Social proof micro */}
            <div className="flex items-center gap-3 sm:gap-4 pt-1">
              <div className="flex -space-x-2 flex-shrink-0">
                {['from-purple-500 to-pink-500', 'from-cyan-500 to-blue-500', 'from-amber-500 to-orange-500', 'from-green-500 to-teal-500', 'from-rose-500 to-pink-500'].map((gradient, i) => (
                  <div
                    key={i}
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br ${gradient} border-2 border-background flex items-center justify-center text-[9px] sm:text-[10px] font-bold text-white`}
                  >
                    {['M', 'A', 'D', 'P', 'K'][i]}
                  </div>
                ))}
              </div>
              <div>
                <p className="text-xs sm:text-sm font-semibold">Trusted by 42,000+ creators</p>
                <div className="flex items-center gap-0.5 sm:gap-1">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-amber-400 fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                  <span className="text-[10px] sm:text-xs text-muted-foreground ml-1">4.9/5 · 3,200 reviews</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Studio Preview — hidden on small mobile, shown from sm */}
          <div className="relative hidden sm:block">
            {/* Glow */}
            <div className="absolute inset-0 bg-primary/10 rounded-3xl blur-3xl scale-110 pointer-events-none" />

            {/* Studio window */}
            <div className="relative bg-zinc-900 rounded-2xl border border-border overflow-hidden shadow-2xl">
              {/* Studio titlebar */}
              <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-zinc-950/80 border-b border-border">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1 sm:gap-1.5">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500" />
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-amber-500" />
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-500" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-muted-foreground ml-1 sm:ml-2 truncate max-w-[120px] sm:max-w-none">The Future of Tech — Ep. 47</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs font-mono text-red-400">
                    <span className="w-1.5 h-1.5 bg-red-400 rounded-full live-pulse" />
                    REC {formatTime(recTime)}
                  </span>
                </div>
              </div>

              {/* Video grid */}
              <div className="grid grid-cols-2 gap-0.5 p-0.5 bg-zinc-950">
                {participants.map((p, i) => (
                  <div
                    key={p.name}
                    className={`relative aspect-video bg-zinc-900 flex items-center justify-center overflow-hidden ${
                      speakingIndex === i ? 'ring-2 ring-inset ring-primary' : ''
                    }`}
                  >
                    <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br ${p.color} flex items-center justify-center text-sm sm:text-lg font-bold text-white shadow-lg`}>
                      {p.initials}
                    </div>
                    <div className="absolute bottom-1.5 sm:bottom-2 left-1.5 sm:left-2 flex items-center gap-1 sm:gap-1.5">
                      <span className="text-[9px] sm:text-xs font-semibold bg-black/60 backdrop-blur-sm px-1.5 sm:px-2 py-0.5 rounded-md text-white">
                        {p.name}
                      </span>
                    </div>
                    <div className="absolute top-1.5 sm:top-2 right-1.5 sm:right-2">
                      <span className="text-[8px] sm:text-[9px] font-mono bg-black/60 backdrop-blur-sm px-1 sm:px-1.5 py-0.5 rounded text-green-400">4K</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom controls bar */}
              <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-zinc-950/80 border-t border-border">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  {[
                    { icon: '🎙️', label: 'Mic', active: true },
                    { icon: '📹', label: 'Cam', active: true },
                    { icon: '🖥️', label: 'Share', active: false },
                  ].map((ctrl) => (
                    <button
                      key={ctrl.label}
                      className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-medium transition-all ${
                        ctrl.active ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {ctrl.icon} <span className="hidden sm:inline">{ctrl.label}</span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <span className="text-[9px] sm:text-[10px] text-muted-foreground font-mono hidden sm:block">4 guests · 4K</span>
                  <button className="px-2 sm:px-3 py-1 sm:py-1.5 bg-red-500/20 text-red-400 rounded-lg text-[10px] sm:text-xs font-semibold">
                    End
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}